<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'dunningLetter',
    path: '/dunning-letters/:id',
    component: 'DunningLetter',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'DunningLetter',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    id: {
      type: String,
      required: true,
      default: null,
    },
    payload: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const sendMailDropdown = ref(null);
  const generatePdfDropdown = ref(null);

  const $refs = {
    get sendMailDropdown() {
      return sendMailDropdown.value;
    },
    get generatePdfDropdown() {
      return generatePdfDropdown.value;
    },
  };

  const item = ref(null);
  const templates = ref(null);
  const emailTemplates = ref(null);
  const htmlTemplates = ref(null);
  const sendmail = ref(null);
  const printdoc = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'xl',
  }));
  const b_1024078196022266_style = computed(() => ({
    minWidth: '250px',
  }));
  const b_3061418626715996_style = computed(() => ({
    minWidth: '250px',
  }));
  const b_3273883411533724_options = computed(() => [
    {
      value: null,
      text: '-',
    },
    {
      value: 'sent',
      text: $fn.value.pgettext('DunningLetterStatus', 'Sent'),
    },
    {
      value: 'delivered',
      text: $fn.value.pgettext('DunningLetterStatus', 'Delivered'),
    },
    {
      value: 'error',
      text: $fn.value.pgettext('DunningLetterStatus', 'Error'),
    },
  ]);
  const b_2771751501651205_columns = computed(() => [
    {
      name: $fn.value.gettext('Invoice number'),
      field: 'invoice_number',
    },
    {
      name: $fn.value.gettext('Completion date'),
      field: 'invoice_data.fulfillment_date',
      textAlign: 'right',
      type: 'date',
    },
    {
      name: $fn.value.gettext('Due date'),
      field: 'invoice_data.due_date',
      textAlign: 'right',
      type: 'date',
    },
    {
      name: $fn.value.gettext('Amount'),
      field: 'invoice_data.amount',
      textAlign: 'right',
      type: 'number',
    },
    {
      name: $fn.value.gettext('Paid'),
      field: 'invoice_data.paid',
      textAlign: 'right',
      type: 'number',
    },
    {
      name: $fn.value.gettext('Debt'),
      field: 'invoice_data.debt',
      textAlign: 'right',
      type: 'number',
    },
  ]);

  const $data = computed(() => ({
    item: item.value,
    templates: templates.value,
    emailTemplates: emailTemplates.value,
    htmlTemplates: htmlTemplates.value,
    sendmail: sendmail.value,
    printdoc: printdoc.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_1024078196022266_style: b_1024078196022266_style.value,
    b_3061418626715996_style: b_3061418626715996_style.value,
    b_3273883411533724_options: b_3273883411533724_options.value,
    b_2771751501651205_columns: b_2771751501651205_columns.value,
  }));

  const $$load_item = () => {
    return (
      props.payload ||
      (props.id !== null
        ? $fn.value.fetch('read_dunning_letter', {
            asResource: true,
            parameters: { id: props.id },
          })
        : $fn.value.schemaDefaults('DunningLetter'))
    );
  };
  const $$load_templates = () => {
    return $fn.value.fetch('list_template');
  };
  const $$load_emailTemplates = () => {
    return _.filter(templates.value, {
      object_type: 'dunning_letter',
      template_type: 'mjml',
    });
  };
  const $$load_htmlTemplates = () => {
    return _.filter(templates.value, {
      object_type: 'dunning_letter',
      template_type: 'html',
    });
  };
  const $$load_sendmail = () => {
    return {
      email: _.isArray(item.value.customer_data.emails)
        ? _.join(item.value.customer_data.emails, '; ')
        : item.value.customer_data.emails,
      template: _.get(_.first(emailTemplates.value), 'id'),
    };
  };
  const $$load_printdoc = () => {
    return { template: _.get(_.first(htmlTemplates.value), 'id') };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'templates' },
      { name: 'emailTemplates', dependsOn: ['templates'] },
      { name: 'htmlTemplates', dependsOn: ['templates'] },
      { name: 'sendmail', dependsOn: ['item', 'emailTemplates'] },
      { name: 'printdoc', dependsOn: ['htmlTemplates'] },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_1024078196022266_style', nodeId: '1024078196022266' },
      { name: 'b_3061418626715996_style', nodeId: '3061418626715996' },
      { name: 'b_3273883411533724_options', nodeId: '3273883411533724' },
      { name: 'b_2771751501651205_columns', nodeId: '2771751501651205' },
    ],
    loaders: {
      item: $$load_item,
      templates: $$load_templates,
      emailTemplates: $$load_emailTemplates,
      htmlTemplates: $$load_htmlTemplates,
      sendmail: $$load_sendmail,
      printdoc: $$load_printdoc,
    },
    ux: {},
    dataRefs: {
      item,
      templates,
      emailTemplates,
      htmlTemplates,
      sendmail,
      printdoc,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_1024078196022266_style,
      b_3061418626715996_style,
      b_3273883411533724_options,
      b_2771751501651205_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const invoiceRows = computed(() => {
    if (loaded.value) {
      return _.map(item.value.items, (p) => ({
        ...p,
        invoice_data: {
          fulfillment_date: p.invoice_data.completion_date,
          ...p.invoice_data,
        },
      }));
    }
    return null;
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          item.value.snapshot('reference_number') ||
            $fn.value.gettext('Dunning Letter'),
        ],
        icon: $config.value.dunningLetter.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.id,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('id');
    }
  );
  watch(
    () => props.payload,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('payload');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_3wubi9lk3_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2996188254223402_closeComponent_akynq4vsh_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };
  const act_2996188254223402_reloadGrid_l9i7un5os_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'all',
    };
  };
  const act_2996188254223402_request_3ih2wbz3x_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_dunning_letter',
      data: item.value,
    };
  };
  const act_2996188254223402_request_3ih2wbz3x_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };
  const act_5048434852698054_script_fmoezid4d_value_callable = async (
    $event
  ) => {
    return $refs.sendMailDropdown.hide(true);
  };
  const act_5048434852698054_script_fmoezid4d_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5048434852698054_script_fmoezid4d_value_callable($event);
      },
    };
  };
  const act_5048434852698054_reloadGrid_ywznw44tl_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'all',
    };
  };
  const act_7525385716635984_script_t51nd8q9k_value_callable = async (
    $event
  ) => {
    return $refs.generatePdfDropdown.hide(true);
  };
  const act_7525385716635984_script_t51nd8q9k_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7525385716635984_script_t51nd8q9k_value_callable($event);
      },
    };
  };
  const act_7525385716635984_reloadGrid_dhi0jif21_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'all',
    };
  };
  const act_7789867021953041_setData_lrrw2gioj_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: new $fn.value.Resource($event.data.response.data),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_7789867021953041_success_2fj805tv0_getWhen = async (
    $event
  ) => {
    return !!$event.data.response.data;
  };
  const act_7789867021953041_form_o40bzxei7_getActionArgs = async ($event) => {
    return {
      name: 'dunningLetter',
      reset: true,
    };
  };
  const act_7789867021953041_reloadGrid_dc7wrbrfu_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'all',
    };
  };
  const act_7789867021953041_request_j7z6agc8e_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_dunning_letter',
      data: item.value,
    };
  };

  const act_7315092382398562_closeModal_3wubi9lk3 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_3wubi9lk3_getActionArgs,
    });
  const inst_evh_7315092382398562_close_invd6exvy =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_3wubi9lk3,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_invd6exvy],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_e4fretaaa =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_fls7d37yn =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_e4fretaaa,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_fls7d37yn],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_fv5ygyibw =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_zpp7qkjgi =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_fv5ygyibw,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_zpp7qkjgi],
    event: 'close',
  });
  const act_4440454779651433_reloadComponentData_pdlps4hm6 =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reloadData',
    });
  const inst_evh_4440454779651433_clickPreventStop_fzoah6s4u =
    new actionTypes.EventHandlerImpl({
      action: act_4440454779651433_reloadComponentData_pdlps4hm6,
      event: 'click.prevent.stop',
      displayName: 'reloadData',
    });
  const inst_evh_4440454779651433_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4440454779651433_clickPreventStop_fzoah6s4u],
      event: 'click.prevent.stop',
    });
  const act_2996188254223402_closeComponent_akynq4vsh =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2996188254223402_closeComponent_akynq4vsh_getActionArgs,
    });
  const inst_evh_2996188254223402_success_42paqxgay =
    new actionTypes.EventHandlerImpl({
      action: act_2996188254223402_closeComponent_akynq4vsh,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_2996188254223402_reloadGrid_l9i7un5os =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2996188254223402_reloadGrid_l9i7un5os_getActionArgs,
    });
  const inst_evh_2996188254223402_success_ohykkxibz =
    new actionTypes.EventHandlerImpl({
      action: act_2996188254223402_reloadGrid_l9i7un5os,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_2996188254223402_request_3ih2wbz3x = new actionTypes.RequestAction({
    actionArgs: act_2996188254223402_request_3ih2wbz3x_getActionArgs,
    confirm: act_2996188254223402_request_3ih2wbz3x_getConfirm,
    displayName: 'delete',
    events: [
      inst_evh_2996188254223402_success_42paqxgay,
      inst_evh_2996188254223402_success_ohykkxibz,
    ],
  });
  const inst_evh_2996188254223402_clickPreventStop_k35kwxpt1 =
    new actionTypes.EventHandlerImpl({
      action: act_2996188254223402_request_3ih2wbz3x,
      event: 'click.prevent.stop',
      displayName: 'delete',
    });
  const inst_evh_2996188254223402_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2996188254223402_clickPreventStop_k35kwxpt1],
      event: 'click.prevent.stop',
    });
  const act_5048434852698054_script_fmoezid4d = new actionTypes.ScriptAction({
    actionArgs: act_5048434852698054_script_fmoezid4d_getActionArgs,
  });
  const inst_evh_5048434852698054_completed_hjf80u216 =
    new actionTypes.EventHandlerImpl({
      action: act_5048434852698054_script_fmoezid4d,
      event: 'completed',
      displayName: 'script',
    });
  const act_5048434852698054_reloadComponentData_c0xv1rn9x =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5048434852698054_completed_fcfa9txr6 =
    new actionTypes.EventHandlerImpl({
      action: act_5048434852698054_reloadComponentData_c0xv1rn9x,
      event: 'completed',
      displayName: 'reloadComponentData',
    });
  const act_5048434852698054_reloadGrid_ywznw44tl =
    new actionTypes.ReloadGridAction({
      actionArgs: act_5048434852698054_reloadGrid_ywznw44tl_getActionArgs,
    });
  const inst_evh_5048434852698054_completed_8leggr6fd =
    new actionTypes.EventHandlerImpl({
      action: act_5048434852698054_reloadGrid_ywznw44tl,
      event: 'completed',
      displayName: 'reloadGrid',
    });
  const inst_evh_5048434852698054_completed = new actionTypes.EventHandlerGroup(
    {
      handlers: [
        inst_evh_5048434852698054_completed_hjf80u216,
        inst_evh_5048434852698054_completed_fcfa9txr6,
        inst_evh_5048434852698054_completed_8leggr6fd,
      ],
      event: 'completed',
    }
  );
  const act_7525385716635984_script_t51nd8q9k = new actionTypes.ScriptAction({
    actionArgs: act_7525385716635984_script_t51nd8q9k_getActionArgs,
  });
  const inst_evh_7525385716635984_completed_jgg4q5p1c =
    new actionTypes.EventHandlerImpl({
      action: act_7525385716635984_script_t51nd8q9k,
      event: 'completed',
      displayName: 'script',
    });
  const act_7525385716635984_reloadComponentData_koh2s0or2 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7525385716635984_completed_lc2go72qm =
    new actionTypes.EventHandlerImpl({
      action: act_7525385716635984_reloadComponentData_koh2s0or2,
      event: 'completed',
      displayName: 'reloadComponentData',
    });
  const act_7525385716635984_reloadGrid_dhi0jif21 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_7525385716635984_reloadGrid_dhi0jif21_getActionArgs,
    });
  const inst_evh_7525385716635984_completed_kbusottf6 =
    new actionTypes.EventHandlerImpl({
      action: act_7525385716635984_reloadGrid_dhi0jif21,
      event: 'completed',
      displayName: 'reloadGrid',
    });
  const inst_evh_7525385716635984_completed = new actionTypes.EventHandlerGroup(
    {
      handlers: [
        inst_evh_7525385716635984_completed_jgg4q5p1c,
        inst_evh_7525385716635984_completed_lc2go72qm,
        inst_evh_7525385716635984_completed_kbusottf6,
      ],
      event: 'completed',
    }
  );
  const act_7789867021953041_setData_lrrw2gioj = new actionTypes.SetDataAction({
    actionArgs: act_7789867021953041_setData_lrrw2gioj_getActionArgs,
  });
  const inst_evh_7789867021953041_success_2fj805tv0 =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_setData_lrrw2gioj,
      event: 'success',
      when: inst_evh_7789867021953041_success_2fj805tv0_getWhen,
      displayName: 'setData',
    });
  const act_7789867021953041_form_o40bzxei7 = new actionTypes.FormAction({
    actionArgs: act_7789867021953041_form_o40bzxei7_getActionArgs,
  });
  const inst_evh_7789867021953041_success_7f2iqedh5 =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_form_o40bzxei7,
      event: 'success',
      displayName: 'form',
    });
  const act_7789867021953041_reloadGrid_dc7wrbrfu =
    new actionTypes.ReloadGridAction({
      actionArgs: act_7789867021953041_reloadGrid_dc7wrbrfu_getActionArgs,
    });
  const inst_evh_7789867021953041_success_zw1o3gsr1 =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_reloadGrid_dc7wrbrfu,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_7789867021953041_request_j7z6agc8e = new actionTypes.RequestAction({
    actionArgs: act_7789867021953041_request_j7z6agc8e_getActionArgs,
    displayName: 'save',
    events: [
      inst_evh_7789867021953041_success_2fj805tv0,
      inst_evh_7789867021953041_success_7f2iqedh5,
      inst_evh_7789867021953041_success_zw1o3gsr1,
    ],
  });
  const inst_evh_7789867021953041_submit_v45b5yo1g =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_request_j7z6agc8e,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_7789867021953041_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7789867021953041_submit_v45b5yo1g],
    event: 'submit',
  });
  const act_2140198529698299_closeComponent_8w5khvtu3 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2140198529698299_clickPreventStop_ytlga8fpg =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeComponent_8w5khvtu3,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_ytlga8fpg],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_invd6exvy,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_fls7d37yn,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_zpp7qkjgi,
    inst_evh_8564662037462133_close,
    inst_evh_4440454779651433_clickPreventStop_fzoah6s4u,
    reloadData: inst_evh_4440454779651433_clickPreventStop_fzoah6s4u,
    inst_evh_4440454779651433_clickPreventStop,
    inst_evh_2996188254223402_success_42paqxgay,
    inst_evh_2996188254223402_success_ohykkxibz,
    inst_evh_2996188254223402_clickPreventStop_k35kwxpt1,
    delete: inst_evh_2996188254223402_clickPreventStop_k35kwxpt1,
    inst_evh_2996188254223402_clickPreventStop,
    inst_evh_5048434852698054_completed_hjf80u216,
    inst_evh_5048434852698054_completed_fcfa9txr6,
    inst_evh_5048434852698054_completed_8leggr6fd,
    inst_evh_5048434852698054_completed,
    inst_evh_7525385716635984_completed_jgg4q5p1c,
    inst_evh_7525385716635984_completed_lc2go72qm,
    inst_evh_7525385716635984_completed_kbusottf6,
    inst_evh_7525385716635984_completed,
    inst_evh_7789867021953041_success_2fj805tv0,
    inst_evh_7789867021953041_success_7f2iqedh5,
    inst_evh_7789867021953041_success_zw1o3gsr1,
    inst_evh_7789867021953041_submit_v45b5yo1g,
    save: inst_evh_7789867021953041_submit_v45b5yo1g,
    inst_evh_7789867021953041_submit,
    inst_evh_2140198529698299_clickPreventStop_ytlga8fpg,
    inst_evh_2140198529698299_clickPreventStop,
  };

  const evh_7315092382398562_close_invd6exvy = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_invd6exvy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_fls7d37yn = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_fls7d37yn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_zpp7qkjgi = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_zpp7qkjgi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4440454779651433_clickPreventStop_fzoah6s4u = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4440454779651433_clickPreventStop_fzoah6s4u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4440454779651433_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4440454779651433_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2996188254223402_success_42paqxgay = async (event, scope) => {
    return await actions.inst_evh_2996188254223402_success_42paqxgay.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2996188254223402_success_ohykkxibz = async (event, scope) => {
    return await actions.inst_evh_2996188254223402_success_ohykkxibz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2996188254223402_clickPreventStop_k35kwxpt1 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2996188254223402_clickPreventStop_k35kwxpt1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2996188254223402_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2996188254223402_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5048434852698054_completed_hjf80u216 = async (event, scope) => {
    return await actions.inst_evh_5048434852698054_completed_hjf80u216.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5048434852698054_completed_fcfa9txr6 = async (event, scope) => {
    return await actions.inst_evh_5048434852698054_completed_fcfa9txr6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5048434852698054_completed_8leggr6fd = async (event, scope) => {
    return await actions.inst_evh_5048434852698054_completed_8leggr6fd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5048434852698054_completed = async (event, scope) => {
    return await actions.inst_evh_5048434852698054_completed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7525385716635984_completed_jgg4q5p1c = async (event, scope) => {
    return await actions.inst_evh_7525385716635984_completed_jgg4q5p1c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7525385716635984_completed_lc2go72qm = async (event, scope) => {
    return await actions.inst_evh_7525385716635984_completed_lc2go72qm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7525385716635984_completed_kbusottf6 = async (event, scope) => {
    return await actions.inst_evh_7525385716635984_completed_kbusottf6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7525385716635984_completed = async (event, scope) => {
    return await actions.inst_evh_7525385716635984_completed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_success_2fj805tv0 = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_success_2fj805tv0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_success_7f2iqedh5 = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_success_7f2iqedh5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_success_zw1o3gsr1 = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_success_zw1o3gsr1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_submit_v45b5yo1g = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_submit_v45b5yo1g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_submit = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_ytlga8fpg = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_ytlga8fpg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    card
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="DunningLetter"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        :color="$config.dunningLetter.color"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <strong class="text-muted text-nowrap" v-if="!isModalComponent && item">
          {{ gettext('Dunning letter') }} - {{ item.reference_number }}
        </strong>
        <div class="btn-toolbar gap-2">
          <ItpButton
            variant="light"
            icon="fa-sync-alt"
            :text="gettext('Reload')"
            :spinning="actions.reloadData.isRunning"
            @click.prevent.stop="
              evh_4440454779651433_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <ItpButton
            type="submit"
            form="u1w012sjv6"
            variant="primary"
            icon="fa-save"
            :text="gettext('Save')"
            :spinning="actions.save.isRunning"
            :disabled="!forms.dunningLetter || !forms.dunningLetter.submittable"
          >
          </ItpButton>
          <ItpButton
            variant="danger"
            icon="fa-trash"
            :text="gettext('Delete')"
            :spinning="actions.delete.isRunning"
            v-if="item.is_deletable"
            @click.prevent.stop="
              evh_2996188254223402_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <BDropdown no-caret auto-close="outside" ref="sendMailDropdown">
            <template #button-content>
              <div class="text-nowrap">
                <ItpIcon name="fa-envelope" class="me-2"> </ItpIcon>
                <span>
                  {{ gettext('Send email') }}
                </span>
                <ItpIcon name="fa-caret-down" class="ms-2"> </ItpIcon>
              </div>
            </template>
            <BDropdownForm :style="b_1024078196022266_style">
              <ItpFormGroup
                name="email"
                :label="gettext('Recipient')"
                :description="
                  gettext('Use semicolon to separate multiple addresses.')
                "
              >
                <ItpFormInput
                  type="text"
                  name="email"
                  :placeholder="gettext('Email address')"
                  size="md"
                  v-model="sendmail.email"
                >
                </ItpFormInput>
              </ItpFormGroup>
              <ItpFormGroup name="template" :label="gettext('Template')">
                <ItpFormNativeSelect
                  name="template"
                  size="md"
                  v-model="sendmail.template"
                  required
                  :options="emailTemplates"
                  valueField="id"
                  textField="name"
                >
                </ItpFormNativeSelect>
              </ItpFormGroup>
              <JobHandlerControl
                jobType="dunning_letters:email"
                :payload="
                  () => ({
                    id: item.id,
                    template: sendmail.template,
                    mail_to: _.chain(_.split(sendmail.email, ';'))
                      .map(_.trim)
                      .filter((p) => !!p)
                      .value(),
                  })
                "
                :text="pgettext('DunningLetter|Submit', 'Send')"
                icon="fa-envelope"
                block
                @completed="evh_5048434852698054_completed($event, {})"
              >
              </JobHandlerControl>
            </BDropdownForm>
          </BDropdown>
          <BDropdown no-caret auto-close="outside" ref="generatePdfDropdown">
            <template #button-content>
              <div class="text-nowrap">
                <ItpIcon name="fa-file-pdf" class="me-2"> </ItpIcon>
                <span>
                  {{ gettext('Generate document') }}
                </span>
                <ItpIcon name="fa-caret-down" class="ms-2"> </ItpIcon>
              </div>
            </template>
            <BDropdownForm :style="b_3061418626715996_style">
              <ItpFormGroup name="template" :label="gettext('Template')">
                <ItpFormNativeSelect
                  name="template"
                  :placeholder="gettext('Template')"
                  size="md"
                  v-model="printdoc.template"
                  required
                  :options="htmlTemplates"
                  valueField="id"
                  textField="name"
                >
                </ItpFormNativeSelect>
              </ItpFormGroup>
              <JobHandlerControl
                jobType="dunning_letters:pdf_generation"
                :payload="() => ({ id: item.id, template: printdoc.template })"
                :text="pgettext('DunningLetter|Submit', 'Generate document')"
                icon="fa-file-pdf"
                block
                @completed="evh_7525385716635984_completed($event, {})"
              >
              </JobHandlerControl>
            </BDropdownForm>
          </BDropdown>
        </div>
        <ItpResourceMeta :item="item" class="ms-auto" v-if="item">
        </ItpResourceMeta>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <div class="row">
        <div class="col-7">
          <ItpForm
            name="dunningLetter"
            id="u1w012sjv6"
            v-if="item && item.id"
            @submit="evh_7789867021953041_submit($event, {})"
          >
            <div class="row">
              <ItpFormGroup
                name="status"
                :label="gettext('Status')"
                class="col-md-4"
              >
                <ItpFormNativeSelect
                  name="status"
                  size="md"
                  v-model="item.status"
                  :options="b_3273883411533724_options"
                >
                </ItpFormNativeSelect>
              </ItpFormGroup>
              <ItpFormGroup
                name="severity"
                :label="gettext('Severity')"
                class="col-md-2"
              >
                <ItpFormInput
                  name="severity"
                  size="md"
                  type="number"
                  :min="1"
                  :max="3"
                  v-model="item.severity"
                >
                </ItpFormInput>
              </ItpFormGroup>
            </div>
            <ItpFormGroup name="notes" :label="gettext('Notes')">
              <ItpFormTextarea name="notes" size="md" v-model="item.notes">
              </ItpFormTextarea>
            </ItpFormGroup>
            <ItpFormGroup name="name" :label="gettext('Client')">
              <ItpFormInput
                name="name"
                size="md"
                v-model="item.customer_name"
                readonly
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup name="address" :label="gettext('Address')">
              <ItpFormInput
                name="address"
                size="md"
                v-model="item.customer_data.address.address"
                readonly
              >
              </ItpFormInput>
            </ItpFormGroup>
            <div class="row">
              <ItpFormGroup :cols-md="4">
                <ItpFormInput
                  name="zipcode"
                  size="md"
                  v-model="item.customer_data.address.postal_code"
                  readonly
                >
                </ItpFormInput>
              </ItpFormGroup>
              <ItpFormGroup :cols-md="8">
                <ItpFormInput
                  name="city"
                  size="md"
                  v-model="item.customer_data.address.city"
                  readonly
                >
                </ItpFormInput>
              </ItpFormGroup>
            </div>
          </ItpForm>
          <div class="form-group">
            <label>
              {{ gettext('Invoices') }}
            </label>
            <ItpTable
              :rows="
                item.items.length > 1
                  ? invoiceRows.concat([
                      {
                        $rowClass: 'fw-bold text-muted',
                        invoice_number: gettext('Total'),
                        invoice_data: {
                          amount: $fn._.sumBy(
                            item.items,
                            (p) => p.invoice_data.amount
                          ),
                          paid: $fn._.sumBy(
                            item.items,
                            (p) => p.invoice_data.paid
                          ),
                          debt: $fn._.sumBy(
                            item.items,
                            (p) => p.invoice_data.debt
                          ),
                        },
                      },
                    ])
                  : invoiceRows
              "
              :columns="b_2771751501651205_columns"
              v-if="item"
            >
            </ItpTable>
          </div>
        </div>
        <div class="col-5">
          <div class="container-fluid" v-if="!item.jobs.length">
            <div class="text-center mt-2" role="alert">
              {{ gettext('No events') }}
            </div>
          </div>
          <div class="container-fluid" v-if="item.jobs.length">
            <div
              v-for="job in $fn._.orderBy(item.jobs, ['number'], ['desc'])"
              :key="job.id"
              class="row"
            >
              <div class="col-4 text-end pb-5 text-nowrap text-truncate">
                <div class="text-nowrap">
                  #{{ job.number }}
                  <ItpIcon v-if="job.job_type === 'email'" name="fa-envelope">
                  </ItpIcon>

                  <ItpIcon
                    v-if="job.job_type === 'document'"
                    name="fa-file-pdf"
                  >
                  </ItpIcon>
                </div>

                <div
                  class="text-muted text-nowrap"
                  :title="datetime(job.created_at)"
                >
                  <ItpIcon name="fa-clock"></ItpIcon>
                  {{ datetime(job.created_at, { distanceToNow: true }) }}
                </div>

                <div class="text-muted text-nowrap">
                  {{ username(job.created_by) }}
                </div>
              </div>

              <div class="col-8 border-start pb-5 text-nowrap text-truncate">
                <div>
                  <!--span :class="jobStatusBadgeClass(job.status)">
        {{pgettext('TaskRunStatus', job.status) }}
      </span-->

                  <span
                    class="ms-1 text-muted text-nowrap"
                    v-if="job.updated_at"
                    :title="datetime(job.updated_at)"
                  >
                    {{ datetime(job.updated_at, { distanceToNow: true }) }}
                  </span>
                </div>

                <div
                  v-if="job.job_type === 'email'"
                  class="text-truncate"
                  :title="job.mail_to"
                >
                  <span class="text-muted me-1">
                    {{ pgettext('email', 'To') }}:
                  </span>
                  {{
                    $fn._.isArray(job.mail_to)
                      ? job.mail_to.join('; ')
                      : job.mail_to
                  }}
                </div>

                <div
                  v-if="job.job_type === 'email' && job.subject"
                  class="text-truncate"
                  :title="job.subject"
                >
                  <span class="text-muted me-1">
                    {{ pgettext('email', 'Subject') }}:
                  </span>
                  {{ job.subject }}
                </div>

                <div
                  v-if="job.job_type === 'email' && job.tracking"
                  :title="datetime(job.tracking.timestamp)"
                >
                  <ItpIcon name="fa-eye"></ItpIcon>
                  <span class="ms-2">
                    {{
                      datetime(job.tracking.timestamp, { distanceToNow: true })
                    }}
                  </span>
                </div>

                <hr
                  v-if="job.log_url || job.pdf_url || job.html_url"
                  class="my-2"
                />

                <div v-if="job.status === 'failed'">
                  <ItpLink
                    :href="job.log_url"
                    icon="fa-file-alt"
                    :text="$i18n._('Error log')"
                    :tooltip="job.log_filename"
                    target="_blank"
                    class="d-block"
                    use-auth
                  ></ItpLink>
                </div>

                <div v-if="job.status === 'completed'">
                  <ItpLink
                    v-if="job.pdf_url"
                    :href="job.pdf_url"
                    icon="fa-file-pdf"
                    text="PDF"
                    :tooltip="job.pdf_filename"
                    target="_blank"
                    class="d-block"
                    use-auth
                  ></ItpLink>

                  <ItpLink
                    v-if="job.html_url"
                    :href="job.html_url"
                    icon="fa-file-code"
                    text="HTML"
                    :tooltip="job.html_filename"
                    target="_blank"
                    class="d-block"
                    use-auth
                  ></ItpLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="modal && loaded">
        <ItpButton
          :text="pgettext('DialogButton', 'Close')"
          variant="default"
          :disabled="actions.save && actions.save.isRunning"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
